import { useState, useCallback, useRef } from "react";
import { useChartsStore } from "../../../store/chartStore";

// Use the same margin constants as ChartWrapper
const CANVAS_MARGIN = { left: 0, right: 55, top: 0, bottom: 30 };

export const useChartResize = (chartId, height, chartConfigs) => {
  const [resizeState, setResizeState] = useState(null);
  const [tempSectionHeights, setTempSectionHeights] = useState(null);

  const lastWindowYRef = useRef(null);

  const getSectionHeights = useChartsStore((state) => state.getSectionHeights);
  const setSectionHeights = useChartsStore((state) => state.setSectionHeights);

  const handleStartResize = useCallback(
    (sectionIndex, startWindowY) => {
      // Initialize synchronous ref for lastWindowY
      lastWindowYRef.current = startWindowY;

      // Get current stored heights or use chart configs as fallback
      const currentSectionHeights = getSectionHeights(chartId) || {};
      const totalCanvasHeight =
        height - CANVAS_MARGIN.top - CANVAS_MARGIN.bottom;

      // Calculate actual pixel heights for all charts, including new ones
      const chartHeights = {};

      chartConfigs.forEach((config) => {
        if (currentSectionHeights[config.index] !== undefined) {
          // Existing section with stored height
          chartHeights[config.index] =
            currentSectionHeights[config.index] * totalCanvasHeight;
        } else {
          // New section - use the height from chartConfigs (calculated by useChartLayout)
          chartHeights[config.index] = config.height;
        }
      });

      // Get the heights for the two charts being resized
      const currentHeight = chartHeights[sectionIndex];
      const nextHeight = chartHeights[sectionIndex + 1];

      if (!currentHeight || !nextHeight) {
        return;
      }

      // Calculate available height for resize (total canvas height minus other sections)
      const otherChartsHeight = Object.entries(chartHeights).reduce(
        (sum, [index, height]) => {
          const idx = parseInt(index);
          // Skip the two charts being resized
          if (idx === sectionIndex || idx === sectionIndex + 1) return sum;
          return sum + height;
        },
        0,
      );

      const availableForResize = totalCanvasHeight - otherChartsHeight;

      const state = {
        sectionIndex,
        startWindowY,
        originalCurrentHeight: currentHeight,
        originalNextHeight: nextHeight,
        currentActualHeight: currentHeight,
        nextActualHeight: nextHeight,
        totalAvailableHeight: availableForResize,
      };

      // Create temp section heights to freeze total canvas height during resize
      const tempHeights = { ...currentSectionHeights };

      // Initialize temp heights with current ratios for all charts
      chartConfigs.forEach((config) => {
        if (!tempHeights[config.index]) {
          // New chart - calculate ratio from its current height
          tempHeights[config.index] = config.height / totalCanvasHeight;
        }
      });

      // Normalize initial ratios to ensure they sum to 1.0
      const initialTotalRatio = Object.values(tempHeights).reduce(
        (sum, ratio) => sum + ratio,
        0,
      );
      if (initialTotalRatio > 0) {
        const normalizationFactor = 1 / initialTotalRatio;
        Object.keys(tempHeights).forEach((key) => {
          tempHeights[key] *= normalizationFactor;
        });
      }

      setTempSectionHeights(tempHeights);
      setResizeState(state);
    },
    [chartConfigs, height, chartId, getSectionHeights],
  );

  const handleResize = useCallback(
    (sectionIndex, windowDeltaY, currentWindowY) => {
      if (!resizeState || resizeState.sectionIndex !== sectionIndex) return;

      // Use synchronous ref for lastWindowY calculation
      const lastWindowY = lastWindowYRef.current;
      const deltaFromLast = currentWindowY - lastWindowY;

      // Don't update store if no incremental movement (prevents bounce)
      if (deltaFromLast === 0) {
        return;
      }

      const { currentActualHeight, nextActualHeight, totalAvailableHeight } =
        resizeState;

      // Calculate new pixel heights using incremental delta
      // Mouse UP (negative delta) should GROW current section (push divider up)
      let newCurrentHeight = currentActualHeight + deltaFromLast;
      let newNextHeight = nextActualHeight - deltaFromLast;

      // Apply minimum height constraints (50px minimum each)
      const minHeight = 50;
      const maxCurrentHeight = totalAvailableHeight - minHeight;
      const maxNextHeight = totalAvailableHeight - minHeight;

      newCurrentHeight = Math.max(
        minHeight,
        Math.min(maxCurrentHeight, newCurrentHeight),
      );
      newNextHeight = Math.max(
        minHeight,
        Math.min(maxNextHeight, newNextHeight),
      );

      // Ensure total height is maintained
      const totalNewHeight = newCurrentHeight + newNextHeight;
      if (totalNewHeight !== currentActualHeight + nextActualHeight) {
        const ratio = (currentActualHeight + nextActualHeight) / totalNewHeight;
        newCurrentHeight *= ratio;
        newNextHeight *= ratio;
      }

      // Convert back to ratios for temporary heights
      const totalCanvasHeight =
        height - CANVAS_MARGIN.top - CANVAS_MARGIN.bottom;
      const newCurrentRatio = newCurrentHeight / totalCanvasHeight;
      const newNextRatio = newNextHeight / totalCanvasHeight;

      // Update only temporary heights, keep total canvas height constant
      // Include ALL sections, not just the ones being resized
      const newTempHeights = { ...tempSectionHeights };

      // Update the ratios for the sections being resized
      newTempHeights[sectionIndex] = newCurrentRatio;
      newTempHeights[sectionIndex + 1] = newNextRatio;

      // Ensure all current chart sections are included
      chartConfigs.forEach((config) => {
        if (!newTempHeights[config.index]) {
          // This section wasn't in temp heights, calculate its ratio from current height
          newTempHeights[config.index] = config.height / totalCanvasHeight;
        }
      });

      // Normalize all ratios to ensure they sum to 1.0
      const totalRatio = Object.values(newTempHeights).reduce(
        (sum, ratio) => sum + ratio,
        0,
      );
      if (totalRatio > 0) {
        const normalizationFactor = 1 / totalRatio;
        Object.keys(newTempHeights).forEach((key) => {
          newTempHeights[key] *= normalizationFactor;
        });
      }

      // Update ref synchronously (no React batching)
      lastWindowYRef.current = currentWindowY;

      // UPDATE ONLY TEMP HEIGHTS: No store updates during resize
      setTempSectionHeights(newTempHeights);

      // Update resize state for tracking
      setResizeState((prev) => ({
        ...prev,
        currentActualHeight: newCurrentHeight,
        nextActualHeight: newNextHeight,
      }));
    },
    [resizeState, tempSectionHeights, height, chartId],
  );

  const handleEndResize = useCallback(() => {
    // Save temp heights to store and clear temp state
    if (tempSectionHeights) {
      setSectionHeights(chartId, tempSectionHeights);
    }

    setResizeState(null);
    setTempSectionHeights(null);

    // Clear ref on resize end
    lastWindowYRef.current = null;
  }, [tempSectionHeights, setSectionHeights, chartId]);

  return {
    resizeState,
    tempSectionHeights,
    handleStartResize,
    handleResize,
    handleEndResize,
  };
};
